<script setup>
import { computed } from "vue";
import { formatCssUnit } from "@/utils/css";
import ButtonApp from "@/components/common/other/ButtonApp.vue";
import Xmark from "@/components/icons/Xmark.vue";
import Exclamation from "@/components/icons/Exclamation.vue";

const props = defineProps({
  text: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "default",
    validator: (value) =>
      ["default", "success", "info", "warning", "error"].includes(value),
  },
  icon: {
    type: String,
    default: "",
  },
  maxWidth: {
    type: [Number, String],
    default: "100%",
  },
  showIcon: {
    type: Boolean,
    default: true,
  },
  closable: {
    type: Boolean,
    default: false,
  },
});

const DEFAULT_ICONS = {
  default: "info",
  success: "check_circle",
  info: "info",
  warning: "warning",
  error: "error",
};

const resolvedIcon = computed(
  () => props.icon || DEFAULT_ICONS[props.type] || "info",
);

const isOpen = defineModel({ type: Boolean, default: true });

const formattedMaxWidth = computed(() => formatCssUnit(props.maxWidth));

function close() {
  isOpen.value = false;
}
</script>

<template>
  <div
    v-if="isOpen"
    class="alert"
    :class="{ [`alert--${type}`]: type !== 'default' }"
  >
    <div class="alert__inner w-100">
      <div class="d-flex align-items-start gap-2">
        <div v-if="showIcon" class="alert__icon d-flex align-items-center">
          <slot name="icon">
            <Exclamation
              v-if="type === 'warning' && !icon"
              class="alert__icon-svg"
            />
            <span v-else class="material-symbols-outlined">{{
              resolvedIcon
            }}</span>
          </slot>
        </div>

        <div class="alert__body">
          <p v-if="title" class="alert__title">{{ title }}</p>
          <div class="text-xs alert__text">
            <slot name="text">
              {{ text }}
            </slot>
          </div>
        </div>

        <ButtonApp
          aria-label="Закрити"
          v-if="closable"
          size="small"
          variant="text"
          class="flex-shrink-0 align-self-start"
          @click="close"
        >
          <template #icon>
            <Xmark class="alert__close" />
          </template>
        </ButtonApp>
      </div>
    </div>
  </div>
</template>

<style scoped>
.alert {
  padding: 0;
  --alert-max-width: v-bind(formattedMaxWidth);
  max-width: var(--alert-max-width);
}

.alert__inner {
  background-color: var(--alert-bg);
  border-radius: 8px;
  padding: 12px;
  display: inline-block;
  border: 1px solid var(--alert-border);
}

.alert--success {
  --alert-text: #458466;
  --alert-bg: #f1f8f4;
  --alert-border: #bfdbca;
}

.alert--info {
  --alert-text: #055160;
  --alert-bg: #cff4fc;
}

.alert--warning {
  --alert-text: #664d03;
  --alert-bg: #fff3cd;
}

.alert--error {
  --alert-text: #842029;
  --alert-bg: #f8d7da;
}

.alert__icon {
  color: var(--alert-text);
}

.alert__icon-svg {
  width: 18px;
  height: 18px;
}

.alert__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.alert__title {
  margin: 0;
  font-weight: 600;
  font-size: 0.8125rem;
  color: var(--alert-text);
  line-height: 1.2;
}

.alert__text {
  color: var(--alert-text);
}

.alert__close {
  width: 16px;
  height: 16px;
  color: var(--alert-text);
}
</style>
